/**
 * FmField — form/control layout primitives, split from FmLayout
 * FmControlSurface, FmFieldGrid, FmField, FmFieldRow.
 */
import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { Rule } from 'antd/es/form';
import { cx, widthTokenFor, type FmContentWidthSize } from './fmLayoutShared';
import { headingLabelText, useHostedHeading } from './fmHostedHeading';
import { FmInline, FmStack, FmText } from './FmComposition';
import { FmChip, type FmChipVariant } from './FmChip';
import { FmTooltip } from './FmTooltip';
import { FmNumberField } from './FmNumberField';
import { FmSelect } from './FmSelect';
import { FmFormItem } from './internal/FmLegacyForm';
import './FmLayout.css';

// FmControlSurface

export interface FmControlSurfaceProps {
  title?: React.ReactNode;
  /**
   * The surrounding surface ALREADY printed this title, so the block renders
   * titleless. Only needed where no published host can
   * reach the conclusion: a titled host that is not an FmSectionBand, or a
   * ReactNode title with no comparable plain text. Inside a titled band the
   * duplicate is dropped, and a distinct title demoted, automatically.
   */
  hosted?: boolean;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: Extract<FmContentWidthSize, 'form' | 'content'>;
  density?: 'comfortable' | 'compact';
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

/**
 * Id of the screen-reader-only node naming a hosted control surface, or
 * undefined when no such node is needed.
 *
 * Only when the heading is suppressed AND the title has no plain text to put in
 * an `aria-label` — a ReactNode title, which is the case `hosted` exists for.
 * Kept out of the component body to stay inside its complexity budget.
 */
function hostedHiddenTitleId(
  showsTitle: boolean,
  title: React.ReactNode,
  generatedId: string,
): string | undefined {
  if (showsTitle || !title) return undefined;
  if (headingLabelText(title) !== undefined) return undefined;
  return `fm-control-surface-${generatedId}-heading`;
}

export const FmControlSurface: React.FC<FmControlSurfaceProps> = ({
  title,
  hosted,
  description,
  actions,
  children,
  footer,
  width = 'form',
  density = 'comfortable',
  className,
  'data-testid': dataTestId,
}) => {
  // One section = one title: a titled host already printed this heading, so the
  // surface either drops it or renders it below the host.
  const heading = useHostedHeading(title, 3, hosted);
  const showsTitle = Boolean(title) && heading.render;
  const HeadingTag = `h${heading.level}` as 'h3' | 'h4' | 'h5' | 'h6';
  const generatedTitleId = React.useId();
  const hiddenTitleId = hostedHiddenTitleId(showsTitle, title, generatedTitleId);
  const hasHeader = showsTitle || description || actions;
  return (
    <section
      className={cx(
        'fm-control-surface',
        density === 'compact' && 'fm-control-surface--compact',
        className,
      )}
      style={{ maxWidth: widthTokenFor(width) }}
      // Titleless still names the region: dropping a DUPLICATE heading must not
      // cost a screen reader the section's name. Plain text names it directly;
      // a ReactNode title has none, so it is carried in the sr-only node below.
      aria-label={showsTitle ? undefined : headingLabelText(title)}
      aria-labelledby={hiddenTitleId}
      data-testid={dataTestId}
    >
      {hiddenTitleId ? (
        <span id={hiddenTitleId} className="sr-only">{title}</span>
      ) : null}
      {hasHeader ? (
        <div className="fm-control-surface__header">
          <div className="fm-control-surface__heading">
            {showsTitle ? (
              <HeadingTag className="fm-control-surface__title">{title}</HeadingTag>
            ) : null}
            {description ? (
              <p className="fm-control-surface__description">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="fm-control-surface__actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className="fm-control-surface__body">{children}</div>
      {footer ? <div className="fm-control-surface__footer">{footer}</div> : null}
    </section>
  );
};

// FmFieldGrid / FmField

export interface FmFieldGridProps {
  /** Target column count on desktop; collapses to 2 then 1 on narrow widths. */
  columns?: 2 | 3 | 4;
  children: React.ReactNode;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

/**
 * Responsive grid of labelled controls inside a control surface. Replaces
 * page-local `display:grid; grid-template-columns: repeat(N, …)` field rows so
 * any control cluster (timber-prices region/window, settings forms) shares one
 * aligned layout and collapse behaviour. AntD controls live inside each
 * `FmField`; the grid owns only rhythm and alignment, never the control.
 */
export const FmFieldGrid: React.FC<FmFieldGridProps> = ({
  columns = 4,
  children,
  className,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx('fm-field-grid', `fm-field-grid--cols-${columns}`, className)}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

export interface FmFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

/** Single labelled control cell for use inside FmFieldGrid. */
export const FmField: React.FC<FmFieldProps> = ({
  label,
  htmlFor,
  children,
  className,
  'data-testid': dataTestId,
}) => (
  <div className={cx('fm-field', className)} data-testid={dataTestId}>
    <label className="fm-field__label" htmlFor={htmlFor}>
      {label}
    </label>
    <div className="fm-field__control">{children}</div>
  </div>
);

// FmFieldRow
//
// SEALED, data-only: every public field a route can
// reach is a string or a typed descriptor — NO ReactNode cells, NO className,
// NO style, NO raw dimensions. The row builds all Fm* nodes (label + tooltip,
// current value + provenance chip, control, help/note) INTERNALLY, so route
// code cannot inject arbitrary structure or local styling through it.

/** Provenance badge shown beside the current value (typed copy, no JSX). */
export interface FmFieldRowProvenance {
  label: string;
  tone?: FmChipVariant;
}

/** Current/baseline value cell descriptor. */
export interface FmFieldRowCurrent {
  /** Formatted current value — NO-FALLBACK: pass an em-dash for missing, never 0. */
  text: string;
  provenance?: FmFieldRowProvenance;
  /** Render muted (baseline/derived comparison) instead of default emphasis. */
  muted?: boolean;
}

/** Option for a select control cell. */
export interface FmFieldRowSelectOption {
  value: string | number;
  label: string;
}

interface FmFieldRowControlBase {
  /**
   * When set, the control binds to the surrounding sealed FmForm field: the row
   * wraps it in the governed FmFormItem (name + validation) internally, so route
   * code never composes the form item itself.
   */
  fieldName?: string;
  testId?: string;
  placeholder?: string;
}

export interface FmFieldRowNumberControl extends FmFieldRowControlBase {
  kind: 'number';
  value?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  /** Field-bound validation callback: returns an error string, or null when valid. */
  validate?: (value: unknown) => string | null;
}

export interface FmFieldRowSelectControl extends FmFieldRowControlBase {
  kind: 'select';
  value?: string | number;
  onChange?: (value: string | number | null) => void;
  options: FmFieldRowSelectOption[];
  allowClear?: boolean;
}

export interface FmFieldRowReadonlyControl {
  kind: 'readonly';
  /** Read-only display copy (e.g. "derived, not editable"). */
  text: string;
  testId?: string;
}

export type FmFieldRowControl =
  | FmFieldRowNumberControl
  | FmFieldRowSelectControl
  | FmFieldRowReadonlyControl;

export interface FmFieldRowProps {
  /** Field label copy (string only — no JSX). */
  label: string;
  /** Optional info-tooltip copy beside the label (governed InfoCircle affordance). */
  labelHint?: string;
  labelHintTestId?: string;
  /** Current/baseline value cell descriptor. */
  current?: FmFieldRowCurrent;
  /** Editor control, expressed as a sealed control descriptor (no JSX). */
  control: FmFieldRowControl;
  /** Trailing guidance copy (string only). Presence selects the four-cell geometry. */
  help?: string;
  /** Secondary guidance note under the help copy (string only). */
  note?: string;
  noteTestId?: string;
  'data-testid'?: string;
}

const renderFieldRowCurrent = (current: FmFieldRowCurrent): React.ReactElement => {
  const value = (
    <FmText as="span" tone={current.muted ? 'muted' : 'default'}>
      {current.text}
    </FmText>
  );
  if (!current.provenance) return value;
  return (
    <FmInline gap="xs" align="center" wrap>
      {value}
      <FmChip variant={current.provenance.tone}>{current.provenance.label}</FmChip>
    </FmInline>
  );
};

const renderFieldRowControl = (control: FmFieldRowControl): React.ReactElement => {
  if (control.kind === 'readonly') {
    return (
      <FmText as="span" tone="muted" data-testid={control.testId}>
        {control.text}
      </FmText>
    );
  }

  const bound = Boolean(control.fieldName);
  const element =
    control.kind === 'number' ? (
      <FmNumberField
        full
        min={control.min}
        max={control.max}
        step={control.step}
        precision={control.precision}
        placeholder={control.placeholder}
        value={bound ? undefined : control.value}
        onChange={bound ? undefined : control.onChange}
        data-testid={control.testId}
      />
    ) : (
      <FmSelect<string | number>
        allowClear={control.allowClear}
        controlWidth="fill"
        placeholder={control.placeholder}
        options={control.options}
        value={bound ? undefined : control.value}
        onChange={
          bound
            ? undefined
            : (val) => control.onChange?.((val as string | number | undefined) ?? null)
        }
        data-testid={control.testId}
      />
    );

  if (!control.fieldName) return element;

  const rules: Rule[] | undefined =
    control.kind === 'number' && control.validate
      ? [
          {
            validator: (_rule, value) => {
              const error = control.validate?.(value);
              return error ? Promise.reject(new Error(error)) : Promise.resolve();
            },
          },
        ]
      : undefined;

  return (
    <FmFormItem
      name={control.fieldName}
      rules={rules}
      validateTrigger={rules ? ['onChange', 'onBlur'] : undefined}
    >
      {element}
    </FmFormItem>
  );
};

/**
 * A labelled comparison-and-edit row: field label, current value, editor
 * control, and optional guidance, aligned across a responsive grid the DS owns.
 * Replaces page-local field-row geometry in
 * correction/edit surfaces (stand corrections workspace, add-correction drawer)
 * so route code never expresses column spans, structure, or styling — it passes
 * typed data only; the row owns rhythm, collapse, and every rendered node.
 */
export const FmFieldRow: React.FC<FmFieldRowProps> = ({
  label,
  labelHint,
  labelHintTestId,
  current,
  control,
  help,
  note,
  noteTestId,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx('fm-field-row', help !== undefined && 'fm-field-row--with-help')}
    data-testid={dataTestId}
  >
    <div className="fm-field-row__label">
      <FmInline gap="xs" align="center">
        <FmText as="span" weight="bold">
          {label}
        </FmText>
        {labelHint !== undefined ? (
          <FmTooltip title={labelHint}>
            <InfoCircleOutlined data-testid={labelHintTestId} />
          </FmTooltip>
        ) : null}
      </FmInline>
    </div>
    <div className="fm-field-row__current">
      {current ? renderFieldRowCurrent(current) : null}
    </div>
    <div className="fm-field-row__control">{renderFieldRowControl(control)}</div>
    {help !== undefined ? (
      <div className="fm-field-row__help">
        <FmText as="span" tone="muted">
          {help}
        </FmText>
        {note !== undefined ? (
          <FmStack gap="xs">
            <FmText as="span" tone="muted" data-testid={noteTestId}>
              {note}
            </FmText>
          </FmStack>
        ) : null}
      </div>
    ) : null}
  </div>
);
